import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TrackingUiAction } from 'src/app/developer/models/tracking-ui-action';
import { TrackingActionDocument } from '../models/tracking-action-document';
import { TrackingService } from '../services/tracking.service';
import { TrackingActionValidateResult } from '../models/tracking-action-validate-result';
import { DataFieldGroup } from 'src/app/shared/datafields/models';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';

import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoPagingModule, DxiColumnModule } from 'devextreme-angular/ui/nested';

type DialogAction = () => Promise<void>;
interface DialogState {
  Title: string;
  SubTitle: string;
  ReadyForAction: boolean;
  ShowInputFields: boolean;
  OkEnabled: boolean;
  OkLabel: string;
  OkAction: DialogAction;
  ShowRejected: boolean;
}

@Component({
    templateUrl: './run-tracking-ui-action-dialog.component.html',
    styleUrls: ['./run-tracking-ui-action-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, DataFieldGroupContainerComponent, DxDataGridModule, BizDataGridDirective, DxoPagingModule, DxiColumnModule]
})
export class RunTrackingUiActionDialogComponent implements OnInit {
  @Input() trackingUiAction: TrackingUiAction;
  @Input() trackingActionDocuments: TrackingActionDocument[];

  state: DialogState = this.getStateInitializing(null);
  form: UntypedFormGroup = new UntypedFormGroup({});
  trackingUiActionRunTimeDisplayGroups: DataFieldGroup[] = [];
  validationResult: TrackingActionValidateResult = null;

  constructor(
    private activeModal: NgbActiveModal,
    private toastService: ToastService,
    private trackingService: TrackingService) {
  }

  ngOnInit(): void {
    this.setStateInitializing();
    this.ngOnInitAsync();
  }

  async ngOnInitAsync(): Promise<void> {
    try {
      this.validationResult = await this.trackingService.validateTrackingAction(this.trackingUiAction.Id, this.trackingActionDocuments);
    }
    catch {
      this.setStateValidationFailed(this);
      return;
    }

    this.trackingUiActionRunTimeDisplayGroups = await this.trackingService.getTrackingUiActionRuntimeDataFieldGroups(this.trackingUiAction.ArtifactId);

    if (this.validationResult.Rejected?.length > 0) {
      this.setStateSomeRejected(this);
    }
    else if (this.validationResult.Accepted?.length > 0) {
      this.setStateAllAccepted(this);
    }
    else {
      if (this.trackingUiActionRunTimeDisplayGroups?.length > 0) {
        // All documents where accepted
        this.setStateReadyForAction(this);
      }
      else {
        // All documents where accepted and no user input required --> execute the action
        this.performAction(this);
      }
    }
  }

  getStateInitializing(trackingUiAction: TrackingUiAction): DialogState {
    return {
      Title: this.trackingUiAction?.Name ?? '',
      SubTitle: 'Loading, please wait...',
      ReadyForAction: false,
      ShowInputFields: false,
      OkEnabled: false,
      OkLabel: 'Ok',
      OkAction: async () => { },
      ShowRejected: false
    };
  }

  setStateInitializing(): void {
    this.state = this.getStateInitializing(this.trackingUiAction);
  }

  setStateValidationFailed(_this: RunTrackingUiActionDialogComponent): void {
    _this.state = {
      Title: _this.trackingUiAction?.Name ?? '',
      SubTitle: 'Operation is cancelled, since an error occurred while validating the selected documents.',
      ReadyForAction: false,
      ShowInputFields: false,
      OkEnabled: false,
      OkLabel: 'Ok',
      OkAction: async () => { },
      ShowRejected: false
    };
  }

  setStateSomeRejected(_this: RunTrackingUiActionDialogComponent): void {
    const acceptedCount: number = this.validationResult.Accepted?.length ?? 0;
    const rejectedCount: number = this.validationResult.Rejected?.length ?? 0;
    const totalCount: number = acceptedCount + rejectedCount;
    _this.state = {
      Title: _this.trackingUiAction?.Name ?? '',
      SubTitle: `${totalCount} document${totalCount === 1 ? '' : 's'} selected for ${_this.trackingUiAction?.Name ?? 'action'}`,
      ReadyForAction: false,
      ShowInputFields: false,
      OkEnabled: !!acceptedCount,
      OkLabel: 'Continue with accepted',
      OkAction: async () => {
        if (this.trackingUiActionRunTimeDisplayGroups?.length > 0) {
          // All documents where accepted
          this.setStateReadyForAction(this);
        }
        else {
          // All documents where accepted and no user input required --> execute the action
          this.performAction(this);
        }
      },
      ShowRejected: true
    };
  }

  setStateAllAccepted(_this: RunTrackingUiActionDialogComponent): void {
    _this.state = {
      Title: _this.trackingUiAction?.Name ?? '',
      SubTitle: null,
      ReadyForAction: false,
      ShowInputFields: false,
      OkEnabled: true,
      OkLabel: 'Continue',
      OkAction: async () => {
        if (this.trackingUiActionRunTimeDisplayGroups?.length > 0) {
          // All documents where accepted
          this.setStateReadyForAction(this);
        }
        else {
          // All documents where accepted and no user input required --> execute the action
          this.performAction(this);
        }
      },
      ShowRejected: false
    };
  }

  setStateReadyForAction(_this: RunTrackingUiActionDialogComponent): void {
    _this.state = {
      Title: _this.trackingUiAction?.Name ?? '',
      SubTitle: 'Apply the following to selected documents',
      ReadyForAction: true,
      ShowInputFields: true,
      OkEnabled: true,
      OkLabel: 'Complete action',
      OkAction: async () => await _this.performAction(_this),
      ShowRejected: false
    };
  }

  setStateExceptionInAction(_this: RunTrackingUiActionDialogComponent): void {
    _this.state = {
      Title: _this.trackingUiAction?.Name ?? '',
      SubTitle: 'An error occurred while executing a custom action. Depending on the implementation, the operation may have partially succeeded.',
      ReadyForAction: false,
      ShowInputFields: false,
      OkEnabled: false,
      OkLabel: 'Ok',
      OkAction: async () => { },
      ShowRejected: false
    };
  }

  async performAction(_this: RunTrackingUiActionDialogComponent) {
    const documents: TrackingActionDocument[] = [];
    _this.validationResult.Accepted?.forEach(a => {
      documents.push(a.Document);
    });
    const datafieldValues = DataFieldGroup.getDataFieldValues(this.trackingUiActionRunTimeDisplayGroups, false);
    try {
      const result = await _this.trackingService.performTrackingAction(_this.trackingUiAction, documents, datafieldValues);
      _this.toastService.showToast(result.StatusText, result.Success ? ToastType.success : ToastType.error, 5000);
      _this.close(_this);
    }
    catch {
      _this.setStateExceptionInAction(_this);
    }
  }

  ok(): void {
    this.state.OkAction();
  }

  cancel(): void {
    this.activeModal.close();
  }

  close(_this: RunTrackingUiActionDialogComponent) {
    _this.activeModal.close();
  }
}
